/**
 * Domain types for defect overview (zoomed-out) grid tiles.
 */

export interface DefectOverviewGridBand {
  readonly layerId: string;
  readonly style: string;
  readonly minZoom: number;
  readonly maxZoom: number;
}

export interface DefectOverviewGridConfig {
  readonly serviceUrl: string;
  readonly tileMatrixSet: string;
  readonly format: string;
  readonly bands: readonly DefectOverviewGridBand[];
  readonly layerEffect?: string;
}

/**
 * Pick the overview band for the current zoom.
 * Returns null at/above street level (full vectors take over).
 */
export function selectDefectOverviewBand(
  config: DefectOverviewGridConfig,
  zoom: number,
  streetLevelZoom: number,
): DefectOverviewGridBand | null {
  if (!Number.isFinite(zoom) || zoom >= streetLevelZoom) {
    return null;
  }
  for (const band of config.bands) {
    if (zoom >= band.minZoom && zoom < band.maxZoom) {
      return band;
    }
  }
  return config.bands[config.bands.length - 1] ?? null;
}

/** Default map zoom when opening an overview-backed defect overlay. */
export function getDefectOverviewInitialZoom(
  config: DefectOverviewGridConfig,
  fallbackStreetZoom: number,
): number {
  const secondBand = config.bands[1];
  if (secondBand) {
    return secondBand.minZoom;
  }
  const firstBand = config.bands[0];
  if (!firstBand) {
    return fallbackStreetZoom;
  }
  return Math.floor((firstBand.minZoom + firstBand.maxZoom) / 2);
}
